import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import api from "../services/api";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { parseApiDateTime } from "../utils/datetime";

const initialForm = {
  item_id: "",
  collaborator_id: "",
  ticket_id: "",
  requester_name: "",
  department: "",
  cost_center: "",
  quantity: "",
  notes: "",
};

const statusStyles = {
  pendente: "bg-amber-100 text-amber-700",
  atendida: "bg-green-100 text-green-700",
  cancelada: "bg-gray-100 text-gray-700",
};

const statusLabels = {
  pendente: "Pendente",
  atendida: "Atendida",
  cancelada: "Cancelada",
};

const StockRequests = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, requestsResponse, collaboratorsResponse, ticketsResponse] = await Promise.all([
        api.get("/stock/items", { params: { include_inactive: false, limit: 1000 } }),
        api.get("/stock/requests", { params: statusFilter ? { status_filter: statusFilter } : {} }),
        api.get("/collaborators/listbox"),
        api.get("/tickets/", { params: { limit: 200 } }),
      ]);
      setItems(Array.isArray(itemsResponse.data) ? itemsResponse.data : []);
      setRequests(Array.isArray(requestsResponse.data) ? requestsResponse.data : []);
      setCollaborators(Array.isArray(collaboratorsResponse.data) ? collaboratorsResponse.data : []);
      setTickets(Array.isArray(ticketsResponse.data) ? ticketsResponse.data : []);
    } catch (error) {
      console.error("Erro ao carregar requisições:", error);
      alert("Erro ao carregar requisições de estoque.");
      setItems([]);
      setCollaborators([]);
      setTickets([]);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const selectableItems = useMemo(
    () => items.filter((item) => item.is_active !== false),
    [items]
  );

  const handleCollaboratorChange = (collaboratorId) => {
    const selected = collaborators.find((collaborator) => String(collaborator.id) === String(collaboratorId));
    setForm((prev) => ({
      ...prev,
      collaborator_id: collaboratorId,
      requester_name: selected
        ? `${selected.first_name || ""} ${selected.last_name || ""}`.trim()
        : prev.requester_name,
      department: selected?.department || prev.department,
      cost_center: selected?.cost_center || prev.cost_center,
    }));
  };

  const handleTicketChange = (ticketId) => {
    const selected = tickets.find((ticket) => String(ticket.id) === String(ticketId));
    setForm((prev) => ({
      ...prev,
      ticket_id: ticketId,
      requester_name: selected?.requester_name || prev.requester_name,
    }));
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/stock/requests", {
        ...form,
        item_id: Number(form.item_id),
        collaborator_id: form.collaborator_id ? Number(form.collaborator_id) : null,
        ticket_id: form.ticket_id ? Number(form.ticket_id) : null,
        quantity: Number(form.quantity || 0),
      });
      setForm(initialForm);
      await loadData();
    } catch (error) {
      console.error("Erro ao criar requisição:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao criar requisição.");
    } finally {
      setSaving(false);
    }
  };

  const fulfillRequest = async (requestId) => {
    try {
      await api.post(`/stock/requests/${requestId}/fulfill`);
      await loadData();
    } catch (error) {
      console.error("Erro ao atender requisição:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao atender requisição.");
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await api.post(`/stock/requests/${requestId}/cancel`);
      await loadData();
    } catch (error) {
      console.error("Erro ao cancelar requisição:", error.response?.data || error);
      alert(error.response?.data?.detail || "Erro ao cancelar requisição.");
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" aria-label="Voltar" onClick={() => navigate("/stock")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Requisições de Estoque</h1>
            <p className="text-gray-500">Solicite materiais e atenda saídas com rastreabilidade.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[420px_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Nova Requisição</h2>
                <p className="text-sm text-gray-500">Registre a necessidade antes da saída.</p>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={submitRequest} className="space-y-3">
                <select
                  value={form.item_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, item_id: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  required
                >
                  <option value="">Selecione o item</option>
                  {selectableItems.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} | Saldo: {item.quantity} {item.unit || "Un"}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Solicitante"
                  value={form.requester_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, requester_name: e.target.value }))}
                  required
                />
                <select
                  value={form.collaborator_id}
                  onChange={(e) => handleCollaboratorChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Vincular colaborador (opcional)</option>
                  {collaborators.map((collaborator) => (
                    <option key={collaborator.id} value={collaborator.id}>
                      {`${collaborator.first_name || ""} ${collaborator.last_name || ""}`.trim()}
                    </option>
                  ))}
                </select>
                <select
                  value={form.ticket_id}
                  onChange={(e) => handleTicketChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Vincular chamado (opcional)</option>
                  {tickets.map((ticket) => (
                    <option key={ticket.id} value={ticket.id}>
                      #{ticket.id} - {ticket.title}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Departamento / Setor"
                  value={form.department}
                  onChange={(e) => setForm((prev) => ({ ...prev, department: e.target.value }))}
                />
                <Input
                  placeholder="Centro de custo"
                  value={form.cost_center}
                  onChange={(e) => setForm((prev) => ({ ...prev, cost_center: e.target.value }))}
                />
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  placeholder="Quantidade"
                  value={form.quantity}
                  onChange={(e) => setForm((prev) => ({ ...prev, quantity: e.target.value }))}
                  required
                />
                <textarea
                  placeholder="Observações"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  className="min-h-[96px] w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <Button type="submit" disabled={saving}>
                  {saving ? "Salvando..." : "Criar requisição"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Fila de Requisições</h2>
                  <p className="text-sm text-gray-500">Acompanhe pendências e atendimentos.</p>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Todos os status</option>
                  <option value="pendente">Pendentes</option>
                  <option value="atendida">Atendidas</option>
                  <option value="cancelada">Canceladas</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-8 text-center text-gray-500">Carregando requisições...</div>
              ) : requests.length === 0 ? (
                <div className="py-8 text-center text-gray-500">Nenhuma requisição encontrada.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Item</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Solicitante</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Vínculos</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Qtd</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Criada em</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-600">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id} className="border-b border-gray-100 align-top">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="font-medium">{request.item_name || "Item removido"}</div>
                            <div className="text-xs text-gray-500">{request.item_sku || "Sem SKU"}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div>{request.requester_name}</div>
                            <div className="text-xs text-gray-500">
                              {[request.department, request.cost_center].filter(Boolean).join(" | ") || "—"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div>{request.collaborator_name || "Sem colaborador"}</div>
                            <div className="text-xs text-gray-500">
                              {request.ticket_title ? `#${request.ticket_id} - ${request.ticket_title}` : "Sem chamado"}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{request.quantity}</td>
                          <td className="px-4 py-3 text-sm">
                            <span
                              className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                statusStyles[request.status] || "bg-gray-100 text-gray-700"
                              }`}
                            >
                              {statusLabels[request.status] || request.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {parseApiDateTime(request.created_at)?.toLocaleString("pt-BR", {
                              timeZone: "America/Sao_Paulo",
                            }) || "—"}
                          </td>
                          <td className="px-4 py-3 text-right text-sm">
                            <div className="flex justify-end gap-2">
                              {request.status === "pendente" && (
                                <>
                                  <Button
                                    variant="outline"
                                    onClick={() => fulfillRequest(request.id)}
                                    className="h-auto px-3 py-1 text-xs border-green-200 text-green-700 hover:bg-green-50"
                                  >
                                    Atender
                                  </Button>
                                  <Button
                                    variant="outline"
                                    onClick={() => cancelRequest(request.id)}
                                    className="h-auto px-3 py-1 text-xs text-gray-700"
                                  >
                                    Cancelar
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default StockRequests;
